import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Store, MapPin, Search, Filter, User, Upload, CheckCircle2, AlertCircle } from "lucide-react";
import RegionMap from "@/components/RegionMap";
import EnseigneLogo from "@/components/EnseigneLogo";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import FilterMultiSelect, { SearchSelect } from "@/components/filters/FilterMultiSelect";

const ENSEIGNE_COLOR = {
  "Leclerc": "bg-blue-100 text-blue-700",
  "Intermarché": "bg-red-100 text-red-700",
  "Système U": "bg-emerald-100 text-emerald-700",
  "Carrefour": "bg-sky-100 text-sky-700",
  "Carrefour R": "bg-sky-100 text-sky-700",
};

export default function Stores() {
  const nav = useNavigate();
  const { user } = useAuth();
  const [stores, setStores] = useState([]);
  const [sectors, setSectors] = useState([]);
  const [users, setUsers] = useState([]);
  const [geoPerf, setGeoPerf] = useState([]);
  const [q, setQ] = useState("");
  const [sector, setSector] = useState([]);
  const [enseigne, setEnseigne] = useState([]);
  const [responsible, setResponsible] = useState([]);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [geo, setGeo] = useState(null);
  const loadGeo = async () => { try { const { data } = await api.get("/admin/geocode-missing"); setGeo(data); return data; } catch { return null; } };
  useEffect(() => { if (user?.role === "admin" || user?.role === "controller") loadGeo(); }, [user?.role]); // eslint-disable-line
  useEffect(() => {
    if (!geo?.running_job) return;
    const t = setInterval(async () => { const d = await loadGeo(); if (d && !d.running_job) { clearInterval(t); const r = d.last_job?.result; if (r) toast.success(`Géocodage terminé : ${r.stores_geocoded + r.corners_geocoded} localisé(s), ${r.failed_count} introuvable(s)`); api.get("/stores").then(({ data }) => setStores(data)); } }, 4000);
    return () => clearInterval(t);
  }, [geo?.running_job?.id]); // eslint-disable-line
  const runGeocode = async () => {
    try { const { data } = await api.post("/admin/geocode-missing"); if (data.status === "started") { toast.info(`Géocodage lancé (${data.total} éléments, ≈ ${Math.ceil(data.eta_seconds / 60)} min)`); loadGeo(); } else toast.info("Un géocodage est déjà en cours"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Erreur de géocodage"); }
  };
  const geoMissing = (geo?.stores_missing || 0) + (geo?.corners_missing || 0);

  const reload = async () => {
    const { data: s } = await api.get("/stores");
    setStores(s);
  };

  const onFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    setImporting(true);
    setImportResult(null);
    try {
      const { data } = await api.post("/admin/upload-corners-xlsx", fd, {
        headers: { "Content-Type": "multipart/form-data" }
      });
      setImportResult(data);
      toast.success(`Import réussi : ${data.updated} corner(s) mis à jour`);
      await reload();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Échec de l'import");
      setImportResult({ error: err.response?.data?.detail || "Échec" });
    } finally {
      setImporting(false);
      e.target.value = "";
    }
  };

  useEffect(() => {
    (async () => {
      const [{ data: s }, { data: sec }, { data: u }, { data: g }] = await Promise.all([
        api.get("/stores"), api.get("/sectors"), api.get("/users"), api.get("/geo-perf"),
      ]);
      setStores(s); setSectors(sec); setUsers(u); setGeoPerf(g);
    })();
  }, []);

  const enseignes = useMemo(() => [...new Set(stores.map(s => s.enseigne).filter(Boolean))].sort(), [stores]);
  const managers = useMemo(() => users.filter(u => u.role === "manager"), [users]);
  const sectorName = (id) => sectors.find(x => x.id === id)?.name || "—";

  const filtered = stores.filter(s =>
    (!sector.length || sector.includes(s.sector_id)) &&
    (!enseigne.length || enseigne.includes(s.enseigne)) &&
    (!responsible.length || responsible.includes(s.responsible_id)) &&
    (!q || s.name.toLowerCase().includes(q.toLowerCase()) ||
       (s.address || "").toLowerCase().includes(q.toLowerCase()) ||
       (s.ville || "").toLowerCase().includes(q.toLowerCase()))
  );

  return (
    <div className="space-y-6" data-testid="stores-page">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="font-display text-3xl font-bold tracking-tight text-slate-900">Magasins</h1>
          <p className="text-slate-500 mt-1">{stores.length} magasins — filtrez par région, enseigne ou responsable.</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
        {geo && (user?.role === "admin" || user?.role === "controller") && (
          <button onClick={runGeocode} disabled={!!geo.running_job || geoMissing === 0} data-testid="geocode-missing-btn" title={geo.sample?.length ? `Ex. : ${geo.sample.join(", ")}` : "Tous les magasins et corners sont géolocalisés"}
                  className={`flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-semibold border transition-colors ${geo.running_job ? "bg-slate-100 text-slate-500 border-slate-200 cursor-wait" : geoMissing === 0 ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-white text-blue-700 border-blue-200 hover:bg-blue-50"}`}>
            <MapPin className="w-4 h-4"/>
            {geo.running_job ? `Géocodage en cours… (${geo.running_job.filename})` : geoMissing === 0 ? "Tous géolocalisés" : `Géocoder les manquants (${geoMissing})`}
          </button>
        )}
        {user?.role === "admin" && (
          <label data-testid="import-corners-btn"
                 className={`cursor-pointer flex items-center gap-2 px-4 py-2.5 rounded-xl text-white text-sm font-semibold transition-colors ${importing ? "bg-slate-400 cursor-wait" : "bg-slate-900 hover:bg-slate-800"}`}>
            <Upload className="w-4 h-4"/>
            {importing ? "Import en cours…" : "Importer Info_corner.xlsx"}
            <input type="file" accept=".xlsx,.xls" onChange={onFileUpload} disabled={importing}
                   className="hidden" data-testid="import-corners-file"/>
          </label>
        )}
        </div>
      </div>
      {geo?.last_job?.result && !geo.running_job && geo.last_job.result.failed_count > 0 && (
        <div data-testid="geocode-failed" className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-2 text-xs text-amber-800">
          <b>{geo.last_job.result.failed_count}</b> élément(s) introuvable(s) au dernier géocodage : {geo.last_job.result.failed.slice(0, 12).join(", ")}{geo.last_job.result.failed.length > 12 ? "…" : ""}. Renseignez leur adresse dans la fiche magasin.
        </div>
      )}
      {importResult && !importResult.error && (
        <div data-testid="import-result-ok" className="rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 flex items-start gap-2 text-sm">
          <CheckCircle2 className="w-4 h-4 text-emerald-600 mt-0.5 shrink-0"/>
          <div className="flex-1 text-emerald-800">
            <b>{importResult.updated}</b> corner(s) mis à jour sur <b>{importResult.total_excel}</b> lignes Excel.
            {importResult.unmatched_count > 0 && <> · <b className="text-rose-700">{importResult.unmatched_count}</b> non matché(s).</>}
            {importResult.not_in_excel_count > 0 && <> · <b className="text-amber-700">{importResult.not_in_excel_count}</b> store(s) DB absent(s) du fichier.</>}
            {importResult.missing_users?.length > 0 && <div className="text-xs mt-1 text-amber-800">Users manquants : {importResult.missing_users.join(", ")}</div>}
          </div>
        </div>
      )}
      {importResult?.error && (
        <div data-testid="import-result-ko" className="rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 flex items-start gap-2 text-sm text-rose-800">
          <AlertCircle className="w-4 h-4 mt-0.5 shrink-0"/> {importResult.error}
        </div>
      )}

      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[240px] max-w-md">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"/>
          <input data-testid="stores-search" value={q} onChange={(e) => setQ(e.target.value)}
                 placeholder="Rechercher un magasin ou une ville…"
                 className="w-full pl-9 pr-4 py-2.5 rounded-xl border border-slate-200 bg-white text-sm outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-400"/>
        </div>
        <FilterMultiSelect testid="stores-sector" label="Région" placeholder="Toutes les régions" size="xl" minWidth={170}
                           options={sectors.map(s => ({ value: s.id, label: s.name }))} value={sector} onChange={setSector}/>
        <FilterMultiSelect testid="stores-enseigne" label="Enseigne" placeholder="Toutes enseignes" size="xl" minWidth={160}
                           options={enseignes} value={enseigne} onChange={setEnseigne}/>
        <FilterMultiSelect testid="stores-responsible" label="Responsable" placeholder="Tous responsables" size="xl" minWidth={170}
                           options={managers.map(m => ({ value: m.id, label: m.name }))} value={responsible} onChange={setResponsible}/>
        <div className="ml-auto text-sm text-slate-500 font-medium">
          {filtered.length} résultat{filtered.length > 1 ? "s" : ""}
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-2xl p-5">
        <h2 className="font-display text-lg font-semibold text-slate-900 mb-4">Carte des magasins — Performance vs objectif</h2>
        <RegionMap stores={filtered.map((s) => {
                     const g = geoPerf.find(x => x.id === s.id);
                     return { ...s, status: g?.status || "ok", perf: g?.perf, renew_badge: g?.renew_badge };
                   })}
                   onStoreClick={(s) => nav(`/stores/${s.id}`)}
                   height={480}/>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
        {filtered.map(s => (
          <button key={s.id} data-testid={`store-${s.id}`} onClick={() => nav(`/stores/${s.id}`)}
                  className="text-left bg-white border border-slate-200 rounded-2xl p-4 card-hover">
            <div className="flex items-start justify-between gap-2">
              <EnseigneLogo enseigne={s.enseigne} size={44} rounded="lg"/>
              {s.enseigne && (
                <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${ENSEIGNE_COLOR[s.enseigne] || "bg-slate-100 text-slate-700"}`}>{s.enseigne}</span>
              )}
            </div>
            <div className="mt-3 font-semibold text-slate-900 text-sm leading-tight">{s.name}</div>
            <div className="text-xs text-slate-500 mt-1 flex items-center gap-1"><MapPin className="w-3 h-3"/>{s.ville || sectorName(s.sector_id)}</div>
            {s.responsible_name && (
              <div className="text-xs text-slate-500 mt-1 flex items-center gap-1"><User className="w-3 h-3"/>{s.responsible_name}</div>
            )}
          </button>
        ))}
        {filtered.length === 0 && <div className="col-span-full bg-white border border-slate-200 rounded-2xl py-16 text-center text-slate-400">Aucun magasin.</div>}
      </div>
    </div>
  );
}
